import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { useQuery, useMutation } from 'react-apollo'
import { t } from 'src/lib/i18n-react'
import routes from 'src/lib/rails-routes/rails-routes'
import { post } from 'src/lib/util'
import SchedulePage from 'src/pages/SchedulePage/SchedulePage'
import SchedulePagePlaceholder from 'src/pages/SchedulePage/SchedulePagePlaceholder'
import DataFetchError from 'src/ui/DataFetchError/DataFetchError'
import BannerWarning from 'src/ui/Banner/BannerWarning/BannerWarning'
import moment from 'moment-timezone'

const APPOINTMENT_FIELDS_FRAGMENT = gql`
  fragment AppointmentFragment on Appointment {
    id
    chatSessionId
    startTime
    endTime
    status
    hasConfirmedTutor
    hasConfirmedStudent
    startsInSeconds
    isTraining
    language {
      code
      localizedName
    }
    partner {
      id
      avatarUrl
      location
      login
      name
      fluentLanguages {
        code
        localizedName
      }
      tutor {
        isFavorite
        countOfCompletedLiveLessons
      }
    }
  }
`

export const FETCH_APPOINTMENTS = gql`
  ${APPOINTMENT_FIELDS_FRAGMENT}
  query FETCH_APPOINTMENTS(
    $l2Code: LanguageEnum!
    $from: DateTime
    $numWeeks: Int
  ) {
    currentUser {
      compactTimezone
      isOverbooked
      isOverextended
      requestCreditCardWhenBooking
      canPayWithSepa
      tutors(code: $l2Code) {
        name
        login
        id
        avatarUrl
        tutor {
          isFavorite
          bookableSlots(from: $from, numWeeks: $numWeeks) {
            duration
          }
        }
      }
      recordingPrivacyPreference
      upcomingAppointments {
        ...AppointmentFragment
      }
      potentialAppointments {
        id
        startTime
        endTime
        language {
          code
          localizedName
        }
      }
    }
  }
`

export const FETCH_SEARCHING_TRAINING_APPOINTMENTS = gql`
  ${APPOINTMENT_FIELDS_FRAGMENT}
  query FETCH_SEARCHING_TRAINING_APPOINTMENTS {
    currentUser {
      searchingTrainingAppointments {
        isBookable: isTraining
        ...AppointmentFragment
      }
    }
  }
`

const CANCEL_APPOINTMENT = gql`
  mutation CANCEL_APPOINTMENT($appointment: AppointmentUpdateInputType!) {
    response: modifyAppointmentStatus(appointment: $appointment) {
      appointment {
        startTime
        id
      }
      errors {
        message
      }
    }
  }
`

const UPDATE_APPOINTMENT = gql`
  mutation UPDATE_APPOINTMENT($appointment: AppointmentUpdateInputType!) {
    response: modifyAppointmentStatus(appointment: $appointment) {
      appointment {
        startTime
        id
      }
      errors {
        message
      }
    }
  }
`

const UPDATE_POTENTIAL_APPOINTMENT = gql`
  mutation UPDATE_POTENTIAL_APPOINTMENT(
    $potentialAppointment: PotentialAppointmentUpdateInputType!
  ) {
    response: modifyPotentialAppointment(
      potentialAppointment: $potentialAppointment
    ) {
      id
    }
  }
`

const BOOK_APPOINTMENT_AS_TRAINER = gql`
  mutation BOOK_APPOINTMENT($id: ID!) {
    response: bookTrainingAppointment(id: $id) {
      startTime
      id
    }
  }
`

const GET_USER_TIMEZONE = gql`
  query getUserTimezone {
    currentUser {
      timezone
    }
  }
`
const GET_TUTOR_INFO = gql`
  query GET_TUTOR_INFO($l2Code: LanguageEnum!) {
    currentUser {
      tutors(code: $l2Code) {
        name
        login
        avatarUrl
        tutor {
          isFavorite
          isBlocked
          countOfJointLiveLessons
        }
      }
    }
  }
`

const CHANGE_TUTOR_RATING = gql`
  mutation CHANGE_TUTOR_RATING($login: String!, $userTutor: UserTutor!) {
    response: updateUserTutor(login: $login, userTutor: $userTutor) {
      id
      tutor {
        isFavorite
      }
    }
  }
`

const GET_USER_CREDIT_INFO = gql`
  query GET_USER_CREDIT_INFO(
    $l1Code: LanguageEnum!
    $l2Code: LanguageEnum!
  ) {
    currentUser {
      canBookLesson(native: $l1Code, learning: $l2Code)
      availableCredits
      monthlyCredits
      bundleCreditsAvailable
      bundleCreditsExpireAt
      personalSubscription {
        endAt
      }
    }
  }
`

const SchedulePageLazy = ({
  l1Code,
  l2Code,
  onShowSnackbar,
  onRenderHeader,
}) => {
  // Logic to render timezone warning banner as header if necessary
  const responseTimezone = useQuery(GET_USER_TIMEZONE)
  const serverTimezone = responseTimezone?.data?.currentUser?.timezone

  const showTimezoneBanner =
    serverTimezone &&
    moment.tz(serverTimezone).format('Z') !==
      moment.tz(moment.tz.guess()).format('Z')

  useEffect(() => {
    const tzBannerProps = {
      message: t(
        'Your Chatterbug timezone <strong>%{chatterbugTz}</strong> does not match the timezone set on your browser <strong>%{browserTz}</strong>.',
        {
          chatterbugTz: serverTimezone,
          browserTz: moment.tz.guess(),
        }
      ),
      buttonLink: routes.account(),
      buttonMessage: t('Set your timezone'),
    }
    // logic taken from l-schedule-upcoming
    onRenderHeader(showTimezoneBanner && <BannerWarning {...tzBannerProps} />)
  }, [onRenderHeader, serverTimezone, showTimezoneBanner])

  const [cancelAppointment, { loading: cancelMutationLoading }] = useMutation(
    CANCEL_APPOINTMENT
  )

  const [updateAppointment, { loading: updateMutationLoading }] = useMutation(
    UPDATE_APPOINTMENT
  )

  const [
    updatePotentialAppointment,
    { loading: updatePotentialAppointmentLoading },
  ] = useMutation(UPDATE_POTENTIAL_APPOINTMENT)

  const [bookAppointment, { loading: bookMutationLoading }] = useMutation(
    BOOK_APPOINTMENT_AS_TRAINER
  )

  const responseUpcomingAppointment = useQuery(FETCH_APPOINTMENTS, {
    variables: { l2Code, numWeeks: 3 },
    fetchPolicy: 'network-only',
  })

  const responseFreeAppointments = useQuery(
    FETCH_SEARCHING_TRAINING_APPOINTMENTS,
    {
      fetchPolicy: 'network-only',
    }
  )

  const responseAllTutors = useQuery(GET_TUTOR_INFO, {
    variables: { l2Code },
    fetchPolicy: 'network-only',
  })

  const responseCreditInfo = useQuery(GET_USER_CREDIT_INFO, {
    variables: {
      l1Code,
      l2Code,
    },
    fetchPolicy: 'network-only',
  })

  const [
    changeTutorRating,
    { loading: changeTutorRatingMutationLoading },
  ] = useMutation(CHANGE_TUTOR_RATING)

  const tutors = responseUpcomingAppointment?.data?.currentUser?.tutors
  const recordingPrivacyPreference =
    responseUpcomingAppointment?.data?.currentUser?.recordingPrivacyPreference
  const upcomingAppointments =
    responseUpcomingAppointment.data?.currentUser?.upcomingAppointments || null
  const isOverbooked =
    responseUpcomingAppointment.data?.currentUser?.isOverbooked
  const compactTimezone =
    responseUpcomingAppointment.data?.currentUser?.compactTimezone
  const requestCreditCardWhenBooking =
    responseUpcomingAppointment.data?.currentUser?.requestCreditCardWhenBooking
  const canPayWithSepa =
    responseUpcomingAppointment.data?.currentUser?.canPayWithSepa

  const freeTrainingAppointments =
    responseFreeAppointments.data?.currentUser?.searchingTrainingAppointments
  // to decide whether booking a LL is possible for a new user
  const canBookLesson = responseCreditInfo?.data?.currentUser?.canBookLesson
  // tutors for tutor panel, includes favorites with 0 lessons, but not non-favorites with 0 lessons
  const allTutors = responseAllTutors?.data?.currentUser?.tutors?.filter(
    (t) => t.tutor.isFavorite || t.tutor.countOfJointLiveLessons > 0
  )
  const availableCredits = responseCreditInfo?.data?.currentUser?.availableCredits
  const monthlyCredits = responseCreditInfo?.data?.currentUser?.monthlyCredits
  const monthlyCreditsEndAt = responseCreditInfo?.data?.currentUser?.personalSubscription?.endAt
  const bundleCredits = responseCreditInfo?.data?.currentUser?.bundleCreditsAvailable
  const bundleCreditsEndAt = responseCreditInfo?.data?.currentUser?.bundleCreditsExpireAt

  const handleBookAppointmentAsTrainer = async (appointmentId) => {
    if (bookMutationLoading) {
      return
    }
    const errorMessage = t(
      'Error while booking the Live Lesson. Please try again later.'
    )

    const successMessage = t('Your Live Lesson was successfully booked')
    try {
      const options = {
        variables: {
          id: appointmentId,
        },
      }
      const mutationResponse = await bookAppointment(options)

      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        responseUpcomingAppointment.refetch()
        responseFreeAppointments.refetch()
        onShowSnackbar({ message: errorMessage, type: 'error' })
      } else {
        responseUpcomingAppointment.refetch()
        responseFreeAppointments.refetch()
        onShowSnackbar({ message: successMessage, type: 'info' })
      }
    } catch (error) {
      onShowSnackbar({ message: errorMessage, type: 'error' })
    }
  }

  const handleUpdateAppointment = async (
    appointmentId,
    action,
    reason = ''
  ) => {
    if (updateMutationLoading) {
      return
    }

    let errorMessage = t(
      'Error while confirming Live Lesson. Please try again later.'
    )
    let successMessage = t('Your Live Lesson was successfully confirmed')
    if (action === 'REJECT') {
      errorMessage = t(
        'Error while rejecting Live Lesson. Please try again later.'
      )
      successMessage = t('Your Live Lesson was successfully rejected')
    }

    try {
      const options = {
        variables: {
          appointment: { id: appointmentId, action, reason },
        },
      }
      const mutationResponse = await updateAppointment(options)

      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        responseUpcomingAppointment.refetch()
        onShowSnackbar({ message: errorMessage, type: 'error' })
      } else {
        responseUpcomingAppointment.refetch()
        onShowSnackbar({ message: successMessage, type: 'info' })
      }
    } catch (error) {
      onShowSnackbar({ message: errorMessage, type: 'error' })
    }
  }

  const handleUpdatePotentialAppointment = async (
    potentialAppointmentId,
    action
  ) => {
    if (updatePotentialAppointmentLoading) {
      return
    }

    let errorMessage = t(
      'Error while confirming Live Lesson. Please try again later.'
    )
    let successMessage = t('Your Live Lesson was successfully confirmed')
    if (action === 'DISMISS') {
      errorMessage = t(
        "Sorry, we're having some issues. Your Live Lesson has not been dismissed. Please try again later or contact support."
      )
      successMessage = t('Live Lesson was dismissed')
    }

    try {
      const options = {
        variables: {
          potentialAppointment: { id: potentialAppointmentId, action },
        },
      }
      const mutationResponse = await updatePotentialAppointment(options)

      if (!mutationResponse.data?.response) {
        responseUpcomingAppointment.refetch()
        onShowSnackbar({ message: errorMessage, type: 'error' })
      } else {
        responseUpcomingAppointment.refetch()
        onShowSnackbar({ message: successMessage, type: 'info' })
      }
    } catch (error) {
      onShowSnackbar({ message: errorMessage, type: 'error' })
    }
  }

  const handleCancelAppointment = async (
    reason,
    appointment,
    refetchCalendarEvents
  ) => {
    if (cancelMutationLoading) {
      return
    }

    const errorMessage = t(
      'Error while cancelling live lesson. Please try again later.'
    )
    const successMessage = t('Your live lesson was successfully cancelled')

    try {
      const options = {
        variables: {
          appointment: { id: appointment.id, action: 'CANCEL', reason },
        },
      }
      const mutationResponse = await cancelAppointment(options)

      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        responseUpcomingAppointment.refetch()
        refetchCalendarEvents()
        onShowSnackbar({ message: errorMessage, type: 'error' })
      } else {
        responseUpcomingAppointment.refetch()
        refetchCalendarEvents()
        onShowSnackbar({ message: successMessage, type: 'info' })
      }
    } catch (error) {
      onShowSnackbar({ message: errorMessage, type: 'error' })
    }
  }

  const handleBook = async (event, refetchEvents, newPaymentSource) => {
    const errorMessage = t(
      'Error while booking the appointment. Please try again later.'
    )

    const successMessage = t('Live Lesson booked!')
    try {
      const response = await post(
        routes.appointment_book(),
        {
          time: event.extendedProps.time_start,
          tutor_id: event.extendedProps.preferred?.[0] || 0,
          // this takes role of "document.getElementById('new-source').setAttribute('value', paymentId)" in "l-schedule-upcoming"
          // in the old UI -- stripe payment id needs to be set when booking a trial lesson with a paywall
          new_source: newPaymentSource,
          learning_language_code: l2Code,
        },
        'json'
      )
      if (response.data.status === 'error') {
        onShowSnackbar({ message: response.data.notice, type: 'error' })
        refetchEvents()
      } else {
        onShowSnackbar({ message: successMessage, type: 'info' })
        if (recordingPrivacyPreference !== 'undecided') {
          responseUpcomingAppointment.refetch()
          refetchEvents()
        } else {
          window.location = routes.recording_privacy_preference()
        }
      }
    } catch (error) {
      console.error(error)
      onShowSnackbar({
        message: errorMessage,
        type: 'error',
      })
      refetchEvents()
    }
  }

  const handleChangeTutorRating = async (login, newRatingValue) => {
    if (changeTutorRatingMutationLoading) {
      return
    }
    try {
      const options = {
        variables: {
          login: login,
          userTutor: { isFavorite: newRatingValue },
        },
      }
      const mutationResponse = await changeTutorRating(options)
      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        return
      } else {
        responseAllTutors.refetch()
        responseUpcomingAppointment.refetch()
      }
    } catch (error) {
      console.log(error)
      throw Error('Changing tutor rating failed')
    }
  }

  if (responseUpcomingAppointment.loading || responseAllTutors.loading) {
    return <SchedulePagePlaceholder />
  }

  if (upcomingAppointments) {
    return (
      <SchedulePage
        availableCredits={availableCredits}
        monthlyCredits={monthlyCredits}
        monthlyCreditsEndAt={monthlyCreditsEndAt}
        bundleCredits={bundleCredits}
        bundleCreditsEndAt={bundleCreditsEndAt}
        tutors={tutors}
        allTutors={allTutors}
        handleCancelAppointment={handleCancelAppointment}
        upcomingAppointments={upcomingAppointments}
        bookableAppointments={freeTrainingAppointments}
        potentialAppointments={
          responseUpcomingAppointment.data?.currentUser
            ?.potentialAppointments || null
        }
        onBookAsTrainer={handleBookAppointmentAsTrainer}
        onBook={handleBook}
        onUpdateAppointment={handleUpdateAppointment}
        onUpdatePotentialAppointment={handleUpdatePotentialAppointment}
        isOverbooked={isOverbooked}
        isOverextended={
          responseUpcomingAppointment.data?.currentUser?.isOverextended
        }
        collectPaymentDetails={requestCreditCardWhenBooking}
        canBookLesson={canBookLesson}
        onShowSnackbar={onShowSnackbar}
        compactTimezone={compactTimezone}
        onChangeTutorRating={handleChangeTutorRating}
        canPayWithSepa={canPayWithSepa}
      />
    )
  }

  return <DataFetchError graphQLResponse={responseUpcomingAppointment} />
}

SchedulePageLazy.propTypes = {
  l1Code: PropTypes.string.isRequired,
  l2Code: PropTypes.string.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  l2Label: PropTypes.string.isRequired, // learning language
  // eslint-disable-next-line react/no-unused-prop-types
  onShowSnackbar: PropTypes.func.isRequired,
  onRenderHeader: PropTypes.func.isRequired,
}

export default SchedulePageLazy
